import React, { useMemo, useState, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeadphonesIcon, ChevronRightIcon, StarIcon, TrendingUpIcon, UsersIcon, TagIcon, LibraryIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { SearchWithAutocomplete, Suggestion } from '../components/UI/SearchWithAutocomplete';
import { audiobooks as realAudiobooks } from '../AudioBookDetails';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
// Genre configuration
const genreTiles = [{
  name: 'Romance',
  count: '1.2k+',
  image: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=600',
  slug: 'romance',
  color: 'from-pink-600 to-rose-600',
  description: 'Sweet to spicy love stories'
}, {
  name: 'Fantasy',
  count: '850+',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600',
  slug: 'fantasy',
  color: 'from-indigo-600 to-violet-600',
  description: 'Magic, fae, and epic quests'
}, {
  name: 'Sci-Fi',
  count: '600+',
  image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600',
  slug: 'sci-fi',
  color: 'from-blue-600 to-cyan-600',
  description: 'Space, tech, and the future'
}, {
  name: 'Mystery',
  count: '900+',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600',
  slug: 'mystery',
  color: 'from-slate-600 to-gray-600',
  description: 'Puzzles, clues, and whodunits'
}, {
  name: 'Thriller',
  count: '750+',
  image: 'https://images.unsplash.com/photo-1509281373149-e957c6296406?w=600',
  slug: 'thriller',
  color: 'from-red-600 to-orange-600',
  description: 'Edge-of-your-seat suspense'
}, {
  name: 'Horror',
  count: '400+',
  image: 'https://images.unsplash.com/photo-1509557965875-b88c97052f0e?w=600',
  slug: 'horror',
  color: 'from-gray-900 to-black',
  description: 'Chilling and supernatural tales'
}, {
  name: 'Historical Fiction',
  count: '500+',
  image: 'https://images.unsplash.com/photo-1461360370896-922624d12a74?w=600',
  slug: 'historical-fiction',
  color: 'from-amber-700 to-yellow-600',
  description: 'Stories set in the past'
}, {
  name: 'Contemporary',
  count: '1.5k+',
  image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600',
  slug: 'contemporary',
  color: 'from-teal-600 to-emerald-600',
  description: 'Modern life and relationships'
}];
// Build unified search suggestions from audiobook data
const buildSearchSuggestions = (): Suggestion[] => {
  const suggestions: Suggestion[] = [];
  const seenAuthors = new Set<string>();
  const seenNarrators = new Set<string>();
  const seenTropes = new Set<string>();
  const seenGenres = new Set<string>();
  realAudiobooks.forEach(book => {
    // Book titles
    suggestions.push({
      id: `book-${book.title}`,
      label: book.title,
      sublabel: `by ${book.author}`,
      type: 'Audiobook',
      image: book.coverImage
    });
    // Authors
    if (!seenAuthors.has(book.author)) {
      seenAuthors.add(book.author);
      suggestions.push({
        id: `author-${book.author}`,
        label: book.author,
        sublabel: 'Author',
        type: 'Author'
      });
    }
    // Narrators
    book.narrators.forEach(narrator => {
      if (!seenNarrators.has(narrator)) {
        seenNarrators.add(narrator);
        suggestions.push({
          id: `narrator-${narrator}`,
          label: narrator,
          sublabel: 'Narrator',
          type: 'Narrator'
        });
      }
    });
    // Tropes
    if (book.tropes) {
      book.tropes.forEach(trope => {
        if (!seenTropes.has(trope)) {
          seenTropes.add(trope);
          suggestions.push({
            id: `trope-${trope}`,
            label: trope,
            type: 'Trope'
          });
        }
      });
    }
    // Genres & subgenres
    if (!seenGenres.has(book.genre)) {
      seenGenres.add(book.genre);
      suggestions.push({
        id: `genre-${book.genre}`,
        label: book.genre,
        type: 'Genre'
      });
    }
    if (book.subgenres) {
      book.subgenres.forEach(sg => {
        if (!seenGenres.has(sg)) {
          seenGenres.add(sg);
          suggestions.push({
            id: `subgenre-${sg}`,
            label: sg,
            type: 'Subgenre'
          });
        }
      });
    }
    // Relationship types
    if (book.relationshipTypes) {
      book.relationshipTypes.forEach(rt => {
        if (!seenGenres.has(`rt-${rt}`)) {
          seenGenres.add(`rt-${rt}`);
          suggestions.push({
            id: `relationship-${rt}`,
            label: rt,
            sublabel: 'Relationship Type',
            type: 'Tag'
          });
        }
      });
    }
  });
  // Add additional well-known suggestions for richer search
  const extraSuggestions: Suggestion[] = [{
    id: 'trope-slow-burn',
    label: 'Slow Burn',
    type: 'Trope'
  }, {
    id: 'trope-friends-to-lovers',
    label: 'Friends to Lovers',
    type: 'Trope'
  }, {
    id: 'trope-second-chance',
    label: 'Second Chance',
    type: 'Trope'
  }, {
    id: 'trope-forbidden-love',
    label: 'Forbidden Love',
    type: 'Trope'
  }, {
    id: 'trope-found-family',
    label: 'Found Family',
    type: 'Trope'
  }, {
    id: 'trope-one-bed',
    label: 'Only One Bed',
    type: 'Trope'
  }, {
    id: 'tag-dual-pov',
    label: 'Dual POV',
    type: 'Tag'
  }, {
    id: 'tag-hea',
    label: 'Happily Ever After (HEA)',
    type: 'Tag'
  }, {
    id: 'tag-series-complete',
    label: 'Series Complete',
    type: 'Tag'
  }, {
    id: 'tag-standalone',
    label: 'Standalone',
    type: 'Tag'
  }, {
    id: 'narrator-julia-whelan',
    label: 'Julia Whelan',
    sublabel: 'Narrator',
    type: 'Narrator'
  }, {
    id: 'author-sarah-j-maas',
    label: 'Sarah J. Maas',
    sublabel: 'Author',
    type: 'Author'
  }, {
    id: 'author-colleen-hoover',
    label: 'Colleen Hoover',
    sublabel: 'Author',
    type: 'Author'
  }, {
    id: 'author-stephen-king',
    label: 'Stephen King',
    sublabel: 'Author',
    type: 'Author'
  }, {
    id: 'cw-violence',
    label: 'Violence',
    sublabel: 'Content Warning',
    type: 'Content Warning'
  }, {
    id: 'cw-sexual-content',
    label: 'Sexual Content',
    sublabel: 'Content Warning',
    type: 'Content Warning'
  }, {
    id: 'rep-lgbtq',
    label: 'LGBTQ+',
    sublabel: 'Representation',
    type: 'Representation'
  }, {
    id: 'rep-bipoc',
    label: 'BIPOC Characters',
    sublabel: 'Representation',
    type: 'Representation'
  }];
  // Only add extras that aren't already present
  extraSuggestions.forEach(s => {
    if (!suggestions.find(existing => existing.label === s.label)) {
      suggestions.push(s);
    }
  });
  return suggestions;
};
const AudiobookGenres = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const searchSuggestions = useMemo(() => buildSearchSuggestions(), []);
  const handleSearchSelect = (suggestion: Suggestion) => {
    // Navigate based on suggestion type
    if (suggestion.type === 'Audiobook') {
      const slug = suggestion.label.replace(/\s+/g, '-').toLowerCase();
      navigate(`/book/${slug}`);
    } else if (suggestion.type === 'Author') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/author/${slug}`);
    } else if (suggestion.type === 'Narrator') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/narrators/${slug}`);
    } else if (suggestion.type === 'Genre' || suggestion.type === 'Subgenre') {
      // Try to match to a genre slug
      const matchedGenre = genreTiles.find(g => g.name.toLowerCase() === suggestion.label.toLowerCase() || g.slug === suggestion.label.toLowerCase());
      if (matchedGenre) {
        navigate(`/audiobooks/${matchedGenre.slug}`);
      } else {
        navigate(`/search?q=${encodeURIComponent(suggestion.label)}`);
      }
    } else if (suggestion.type === 'Trope') {
      navigate(`/tropes?search=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'Content Warning') {
      navigate(`/content-warnings?search=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'Representation') {
      navigate(`/representation?search=${encodeURIComponent(suggestion.label)}`);
    } else if (suggestion.type === 'Tag') {
      navigate(`/search?q=${encodeURIComponent(suggestion.label)}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion.label)}`);
    }
    setSearchQuery('');
  };
  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };
  const audiobookNavItems: NavItem[] = [{
    label: 'Audiobooks',
    href: '/audiobooks',
    icon: HeadphonesIcon
  }, {
    label: 'Narrators',
    href: '/narrators',
    icon: UsersIcon
  }, {
    label: 'Genres',
    href: '/audiobooks/genres',
    icon: TagIcon,
    isActive: true
  }, {
    label: 'New Releases',
    href: '/audiobooks/new',
    icon: StarIcon
  }, {
    label: 'Bestsellers',
    href: '/audiobooks/bestsellers',
    icon: TrendingUpIcon
  }, {
    label: 'Series',
    href: '/series',
    icon: LibraryIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-5761" data-mp="1vzcs743ykyak">
      {/* Header with Search */}
      <div className={`py-10 ${dk.surface} border-b ${dk.border}`} data-id="element-5762" data-mp="1r0rr72gvual2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5763" data-mp="25ocw91xbijvm">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-5764" data-mp="25qpkfwto5qh4">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-5765" data-mp="20wbmpnrjb79a">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5766" data-mp="2ao909160mbb6" />
            <Link to="/audiobooks" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-5767" data-mp="2apsgirro8mcy">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5768" data-mp="2joxdhtnl47n0" />
            <span className={dk.textPrimary} data-id="element-5769" data-mp="u97srvpd4hay">Genres</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-5770" data-mp="ubap0xh3z6o4">
            <div className="flex items-center gap-3" data-id="element-5771" data-mp="pchd39hgznq2">
              <HeadphonesIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-5772" data-mp="30yrhz0ck2sq8" />
              <div data-id="element-5773" data-mp="35wjkspeqnz97">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-5774" data-mp="35x2trgmevdb3">
                  Audiobook Genres
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-5775" data-mp="2r7dmr4rn0f2w">
                  Browse audiobooks by genre — find your next listen
                </p>
              </div>
            </div>
            <div className="w-full md:max-w-md" data-id="element-5776" data-mp="2i6ficldae5js">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={searchSuggestions} placeholder="Search titles, authors, narrators, tropes..." variant="rounded" onSelect={handleSearchSelect} onSearch={handleSearch} data-id="element-5777" data-mp="2n6bmqlpwbyzv" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5778" data-mp="bh0z1oou8mil">
        <div className="flex gap-8" data-id="element-5779" data-mp="9zlt8jw7set6">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-5780" data-mp="a0n2cito6r89">
            <FilterSidebar title="Browse" navigation={audiobookNavItems} sections={[]} onReset={() => {}} searchable={false} data-id="element-5781" data-mp="38z5kwrbyxw" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-5782" data-mp="nqql2ztcc2sy">
            {/* Genre Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-5783" data-mp="j01sfh5xczs8">
              {genreTiles.map(genre => <Link key={genre.slug} to={`/audiobooks/${genre.slug}`} className="group relative h-48 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" data-id="element-5784" data-mp="exnb51hwm832">
                  <div className="absolute inset-0" data-id="element-5785" data-mp="2pde4mx6e38la">
                    <img src={genre.image} alt={genre.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-5786" data-mp="2pep4ilwprw74" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${genre.color} opacity-75 group-hover:opacity-65 transition-opacity`} data-id="element-5787" data-mp="33cnjntiuzwno"></div>
                  </div>
                  <div className="absolute inset-0 p-5 flex flex-col justify-end" data-id="element-5788" data-mp="3d9jl5facbiiq">
                    <h3 className="text-white font-bold text-xl" data-id="element-5789" data-mp="3i11jrwow570j">
                      {genre.name}
                    </h3>
                    <p className="text-white/80 text-sm mt-1" data-id="element-5790" data-mp="3m1lkfa8oasrf">
                      {genre.description}
                    </p>
                    <div className="flex items-center justify-between mt-3" data-id="element-5791" data-mp="3qzvgdn281xe3">
                      <span className="text-white/70 text-xs font-medium" data-id="element-5792" data-mp="3w1ktd8908l4t">
                        {genre.count} Audiobooks
                      </span>
                      <span className="text-white/80 text-xs font-medium flex items-center gap-1 group-hover:translate-x-1 transition-transform" data-id="element-5793" data-mp="3dfslal8jz7fs">
                        Browse <ChevronRightIcon className="h-3 w-3" data-id="element-5794" data-mp="3s3dm28hygukj" />
                      </span>
                    </div>
                  </div>
                </Link>)}
            </div>

            {/* Quick Discovery Section */}
            <div className="mt-16" data-id="element-5795" data-mp="2ugn47v6y520h">
              <h2 className={`text-2xl font-bold mb-6 ${dk.textHeading}`} data-id="element-5796" data-mp="2uhon2lu7essr">
                Discover by Tag
              </h2>
              <div className="flex flex-wrap gap-2" data-id="element-5797" data-mp="39bktgf6stbdc">
                {[{
                label: 'Enemies to Lovers',
                path: '/tropes?search=Enemies+to+Lovers'
              }, {
                label: 'Fated Mates',
                path: '/tropes?search=Fated+Mates'
              }, {
                label: 'Slow Burn',
                path: '/tropes?search=Slow+Burn'
              }, {
                label: 'Why Choose',
                path: '/relationship-types'
              }, {
                label: 'Fake Dating',
                path: '/tropes?search=Fake+Dating'
              }, {
                label: 'Dark Romance',
                path: '/audiobooks/romance'
              }, {
                label: 'Found Family',
                path: '/tropes?search=Found+Family'
              }, {
                label: 'Academy',
                path: '/tropes?search=Academy'
              }, {
                label: 'Forced Proximity',
                path: '/tropes?search=Forced+Proximity'
              }, {
                label: 'Grumpy/Sunshine',
                path: '/tropes?search=Grumpy'
              }, {
                label: 'LGBTQ+',
                path: '/representation'
              }, {
                label: 'Dual Narration',
                path: '/search?q=Dual+Narration'
              }, {
                label: 'Series Complete',
                path: '/search?q=Series+Complete'
              }, {
                label: 'Spicy (4+ 🌶️)',
                path: '/spice-darkness'
              }].map(tag => <Link key={tag.label} to={tag.path} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${dk.card} border ${dk.border} ${dk.textSecondary} hover:${rt.bgMedium} hover:${rt.textPrimary}`} data-id="element-5798" data-mp="1atwplfna5l16">
                    {tag.label}
                  </Link>)}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>;
};
export default AudiobookGenres;